import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Card, 
  CardContent, 
  TextField, 
  Button, 
  AppBar, 
  Toolbar,
  Chip,
  Badge,
  CircularProgress,
  Snackbar,
  Alert,
  Drawer,
  List,
  ListItem,
  ListItemAvatar,
  IconButton,
  Divider,
  Avatar,
  ListItemText,
  Paper,
  Pagination
} from '@mui/material';
import { styled } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import HomeIcon from '@mui/icons-material/Home';
import { Add, Remove, Delete } from '@mui/icons-material';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../../context/CartContext';
import { productoService } from '../../services/productos';
import type { Producto } from '../../services/productos';
import PublicNavigation from '../navigation/PublicNavigation';

// Funciones helper
const formatPrice = (amount: number): string => {
  return new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'CLP',
    minimumFractionDigits: 0,
  }).format(amount);
};

const getProductImage = (product: Producto): string => {
  return product.imagen || 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=400&h=300&fit=crop&auto=format';
};

// Productos estáticos como fallback (representativo de abarrote chileno)
const publicProducts = [
  // Productos estrella de El Piñón
  {
    id: 1,
    codigo: 'PIN001',
    nombre: 'Piñones Premium 500g',
    descripcion: 'Piñones de araucaria de la mejor calidad, tostados artesanalmente',
    precio_venta: 25000,
    precio_compra: 20000,
    categoria: 1,
    categoria_nombre: 'Frutos Secos',
    stock_actual: 15,
    stock_minimo: 5,
    unidad_medida: 'kg',
    imagen: 'https://images.unsplash.com/photo-1448043552756-e747b7a2b2b8?w=600&h=400&fit=crop',
    activo: true,
    fecha_creacion: '2024-01-15',
    fecha_actualizacion: '2024-01-15',
    necesita_reposicion: false,
    margen_ganancia: 25,
  },
  {
    id: 2,
    codigo: 'MIE001',
    nombre: 'Miel de Araucaria 250ml',
    descripcion: 'Miel pura de flores de araucaria, cosechada artesanalmente',
    precio_venta: 12000,
    precio_compra: 9000,
    categoria: 2,
    categoria_nombre: 'Mieles',
    stock_actual: 8,
    stock_minimo: 3,
    unidad_medida: 'ml',
    imagen: 'https://images.unsplash.com/photo-1471943311424-646960669fbc?w=600&h=400&fit=crop',
    activo: true,
    fecha_creacion: '2024-01-15',
    fecha_actualizacion: '2024-01-15',
    necesita_reposicion: false,
    margen_ganancia: 33,
  },

  // Abarrotes básicos
  {
    id: 3,
    codigo: 'ARR001',
    nombre: 'Arroz Grado 2 - 1kg',
    descripcion: 'Arroz nacional de excelente calidad para el día a día',
    precio_venta: 1800,
    precio_compra: 1400,
    categoria: 3,
    categoria_nombre: 'Granos',
    stock_actual: 45,
    stock_minimo: 10,
    unidad_medida: 'kg',
    imagen: 'https://images.unsplash.com/photo-1586201375761-83865001e31c?w=600&h=400&fit=crop',
    activo: true,
    fecha_creacion: '2024-01-15',
    fecha_actualizacion: '2024-01-15',
    necesita_reposicion: false,
    margen_ganancia: 20,
  },
  {
    id: 4,
    codigo: 'ACE001',
    nombre: 'Aceite Maravilla 1L',
    descripcion: 'Aceite de maravilla ideal para cocinar',
    precio_venta: 2500,
    precio_compra: 2000,
    categoria: 4,
    categoria_nombre: 'Aceites',
    stock_actual: 22,
    stock_minimo: 8,
    unidad_medida: 'L',
    imagen: 'https://images.unsplash.com/photo-1474979266404-7eaacbcd87c5?w=600&h=400&fit=crop',
    activo: true,
    fecha_creacion: '2024-01-15',
    fecha_actualizacion: '2024-01-15',
    necesita_reposicion: false,
    margen_ganancia: 25,
  },
  {
    id: 5,
    codigo: 'AZU001',
    nombre: 'Azúcar Granulada 1kg',
    descripcion: 'Azúcar blanca granulada de primera calidad',
    precio_venta: 1200,
    precio_compra: 900,
    categoria: 5,
    categoria_nombre: 'Condimentos',
    stock_actual: 30,
    stock_minimo: 10,
    unidad_medida: 'kg',
    imagen: 'https://images.unsplash.com/photo-1587735243615-c03f25aaff15?w=600&h=400&fit=crop',
    activo: true,
    fecha_creacion: '2024-01-15',
    fecha_actualizacion: '2024-01-15',
    necesita_reposicion: false,
    margen_ganancia: 30,
  },

  // Lácteos
  {
    id: 6,
    codigo: 'LEC001',
    nombre: 'Leche Entera 1L',
    descripcion: 'Leche fresca entera, pasteurizada',
    precio_venta: 1100,
    precio_compra: 850,
    categoria: 6,
    categoria_nombre: 'Lácteos',
    stock_actual: 18,
    stock_minimo: 12,
    unidad_medida: 'L',
    imagen: 'https://images.unsplash.com/photo-1563636619-e9143da7973b?w=600&h=400&fit=crop',
    activo: true,
    fecha_creacion: '2024-01-15',
    fecha_actualizacion: '2024-01-15',
    necesita_reposicion: true,
  },
  {
    id: 7,
    codigo: 'QUE001',
    nombre: 'Queso Gauda 200g',
    descripcion: 'Queso gauda madurado, ideal para sandwiches',
    precio_venta: 3500,
    precio_compra: 2800,
    categoria: 6,
    categoria_nombre: 'Lácteos',
    stock_actual: 12,
    stock_minimo: 6,
    unidad_medida: 'g',
    imagen: 'https://images.unsplash.com/photo-1486297678162-eb2a19b0a32d?w=600&h=400&fit=crop',
    activo: true,
    fecha_creacion: '2024-01-15',
    fecha_actualizacion: '2024-01-15',
    necesita_reposicion: false,
  },

  // Panadería
  {
    id: 8,
    codigo: 'PAN001',
    nombre: 'Marraqueta',
    descripcion: 'Pan marraqueta fresco del día',
    precio_venta: 150,
    precio_compra: 100,
    categoria: 7,
    categoria_nombre: 'Panadería',
    stock_actual: 24,
    stock_minimo: 20,
    unidad_medida: 'unidad',
    imagen: 'https://images.unsplash.com/photo-1509440159596-0249088772ff?w=600&h=400&fit=crop',
    activo: true,
    fecha_creacion: '2024-01-15',
    fecha_actualizacion: '2024-01-15',
    necesita_reposicion: false,
  },

  // Bebidas
  {
    id: 9,
    codigo: 'COC001',
    nombre: 'Coca Cola 350ml',
    descripcion: 'Bebida gaseosa Coca Cola lata 350ml',
    precio_venta: 800,
    precio_compra: 600,
    categoria: 8,
    categoria_nombre: 'Bebidas',
    stock_actual: 36,
    stock_minimo: 24,
    unidad_medida: 'ml',
    imagen: 'https://images.unsplash.com/photo-1554866585-cd94860890b7?w=600&h=400&fit=crop',
    activo: true,
    fecha_creacion: '2024-01-15',
    fecha_actualizacion: '2024-01-15',
    necesita_reposicion: false,
  },
  {
    id: 10,
    codigo: 'AGU001',
    nombre: 'Agua Mineral 500ml',
    descripcion: 'Agua mineral natural sin gas',
    precio_venta: 600,
    precio_compra: 400,
    categoria: 8,
    categoria_nombre: 'Bebidas',
    stock_actual: 28,
    stock_minimo: 15,
    unidad_medida: 'ml',
    imagen: 'https://images.unsplash.com/photo-1548839140-29a749e1cf4d?w=600&h=400&fit=crop',
    activo: true,
    fecha_creacion: '2024-01-15',
    fecha_actualizacion: '2024-01-15',
    necesita_reposicion: false,
  },

  // Frutas y Verduras
  {
    id: 11,
    codigo: 'PAL001',
    nombre: 'Palta Hass',
    descripcion: 'Paltas Hass de primera calidad, perfecta maduración',
    precio_venta: 400,
    precio_compra: 300,
    categoria: 9,
    categoria_nombre: 'Frutas',
    stock_actual: 0,
    stock_minimo: 15,
    unidad_medida: 'unidad',
    imagen: 'https://images.unsplash.com/photo-1523049673857-eb18f1d7b578?w=600&h=400&fit=crop',
    activo: true,
    fecha_creacion: '2024-01-15',
    fecha_actualizacion: '2024-01-15',
    necesita_reposicion: true,
  },
  {
    id: 12,
    codigo: 'TOM001',
    nombre: 'Tomate 1kg',
    descripcion: 'Tomates frescos de temporada',
    precio_venta: 2200,
    precio_compra: 1800,
    categoria: 10,
    categoria_nombre: 'Verduras',
    stock_actual: 8,
    stock_minimo: 10,
    unidad_medida: 'kg',
    imagen: 'https://images.unsplash.com/photo-1546470427-e6e5c0b47de9?w=600&h=400&fit=crop',
    activo: true,
    fecha_creacion: '2024-01-15',
    fecha_actualizacion: '2024-01-15',
    necesita_reposicion: true,
  },

  // Legumbres
  {
    id: 13,
    codigo: 'LEN001',
    nombre: 'Lentejas 500g',
    descripcion: 'Lentejas secas de excelente calidad',
    precio_venta: 1800,
    precio_compra: 1400,
    categoria: 3,
    categoria_nombre: 'Granos',
    stock_actual: 20,
    stock_minimo: 8,
    unidad_medida: 'g',
    imagen: 'https://images.unsplash.com/photo-1583838792582-4e7b01c8c7be?w=600&h=400&fit=crop',
    activo: true,
    fecha_creacion: '2024-01-15',
    fecha_actualizacion: '2024-01-15',
    necesita_reposicion: false,
  },

  // Conservas
  {
    id: 14,
    codigo: 'ATU001',
    nombre: 'Atún en Agua 185g',
    descripcion: 'Atún en agua, bajo en sodio',
    precio_venta: 1500,
    precio_compra: 1200,
    categoria: 11,
    categoria_nombre: 'Conservas',
    stock_actual: 15,
    stock_minimo: 8,
    unidad_medida: 'g',
    imagen: 'https://images.unsplash.com/photo-1599599810769-bcde5a160d32?w=600&h=400&fit=crop',
    activo: true,
    fecha_creacion: '2024-01-15',
    fecha_actualizacion: '2024-01-15',
    necesita_reposicion: false,
  },

  // Snacks
  {
    id: 15,
    codigo: 'GAL001',
    nombre: 'Galletas Soda 150g',
    descripcion: 'Galletas saladas crujientes',
    precio_venta: 900,
    precio_compra: 700,
    categoria: 12,
    categoria_nombre: 'Snacks',
    stock_actual: 25,
    stock_minimo: 12,
    unidad_medida: 'g',
    imagen: 'https://images.unsplash.com/photo-1566478989037-eec170784d0b?w=600&h=400&fit=crop',
    activo: true,
    fecha_creacion: '2024-01-15',
    fecha_actualizacion: '2024-01-15',
    necesita_reposicion: false,
  },
];

// Styled Components
const StyledHeader = styled(AppBar)({
  background: 'linear-gradient(135deg, #4b2e12 0%, #5a3a1e 100%)',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
});

const LogoContainer = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  cursor: 'pointer',
  flexGrow: 1,
});

const LogoImage = styled('img')({
  width: '50px',
  height: '50px',
  borderRadius: '50%',
  objectFit: 'cover',
});

const ProductCard = styled(Card)({
  borderRadius: '15px',
  boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
  overflow: 'hidden',
  transition: 'all 0.3s ease',
  cursor: 'pointer',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
  },
});

const ProductImage = styled('img')({
  width: '100%',
  height: '200px',
  objectFit: 'cover',
  transition: 'transform 0.3s ease',
});

const CatalogContainer = styled(Container)({
  paddingTop: '40px',
  paddingBottom: '40px',
  minHeight: '100vh',
  backgroundColor: '#fffaf2',
});

const SearchContainer = styled(Box)({
  backgroundColor: '#fff',
  borderRadius: '15px',
  padding: '30px',
  marginBottom: '40px',
  boxShadow: '0 4px 15px rgba(0,0,0,0.08)',
});

const AddToCartButton = styled(Button)({
  background: 'linear-gradient(45deg, #2e5a33, #4a7c59)',
  color: 'white',
  borderRadius: '20px',
  fontWeight: 'bold',
  padding: '8px 20px',
  fontSize: '14px',
  marginTop: '10px',
  '&:hover': {
    background: 'linear-gradient(45deg, #1a3d1e, #2e5a33)',
  },
  '&:disabled': {
    background: '#cccccc',
  },
});

const PriceTag = styled(Typography)({
  fontWeight: 700,
  fontSize: '18px',
  color: '#2e5a33',
  marginBottom: '5px',
});

const StockChip = styled(Chip)(({ available }: { available: boolean }) => ({
  backgroundColor: available ? '#e8f5e8' : '#ffebee',
  color: available ? '#2e7d32' : '#c62828',
  fontWeight: 600,
  fontSize: '12px',
  marginBottom: '10px',
}));

const CartDrawer = styled(Drawer)({
  '& .MuiDrawer-paper': {
    width: 400,
    background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
    boxShadow: '0 0 20px rgba(0,0,0,0.1)',
  },
});

const CartHeader = styled(Box)({
  background: 'linear-gradient(135deg, #2e5a33 0%, #4a7c59 100%)',
  color: 'white',
  padding: '20px',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
});

const CartContent = styled(Box)({
  padding: '0',
  height: 'calc(100vh - 140px)', // Espacio para header y footer
  overflowY: 'auto',
});

const CartFooter = styled(Box)({
  padding: '20px',
  background: 'white',
  borderTop: '1px solid #e0e0e0',
  boxShadow: '0 -2px 10px rgba(0,0,0,0.1)',
});

const EmptyCart = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  height: '300px',
  color: '#666',
});

const CartItemCard = styled(Paper)({
  margin: '10px 15px',
  padding: '15px',
  borderRadius: '15px',
  boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
  transition: 'all 0.3s ease',
  '&:hover': {
    boxShadow: '0 4px 15px rgba(0,0,0,0.12)',
  },
});

const CartItemImage = styled(Avatar)({
  width: 60,
  height: 60,
  borderRadius: '12px',
  marginRight: '15px',
});

const QuantityControls = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  background: '#f5f5f5',
  borderRadius: '20px',
  padding: '4px',
});

const QuantityButton = styled(IconButton)({
  backgroundColor: '#2e5a33',
  color: 'white',
  width: '32px',
  height: '32px',
  '&:hover': {
    backgroundColor: '#1a3d1e',
  },
  '&:disabled': {
    backgroundColor: '#cccccc',
  },
});

const TotalSection = styled(Box)({
  background: 'linear-gradient(135deg, #2e5a33 0%, #4a7c59 100%)',
  color: 'white',
  padding: '15px',
  borderRadius: '15px',
  marginBottom: '15px',
});

const CheckoutButton = styled(Button)({
  background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)',
  color: 'white',
  padding: '15px',
  borderRadius: '15px',
  fontWeight: 'bold',
  fontSize: '16px',
  '&:hover': {
    background: 'linear-gradient(135deg, #128C7E 0%, #25D366 100%)',
    transform: 'translateY(-2px)',
  },
});



const ProductCatalog: React.FC = () => {
  const navigate = useNavigate();
  const [productos, setProductos] = useState<Producto[]>([]);
  const [filteredProductos, setFilteredProductos] = useState<Producto[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [cartOpen, setCartOpen] = useState(false);
  
  // Paginación
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 8;
  
  const { items, addToCart, removeFromCart, updateQuantity, clearCart, getTotalItems, getTotalPrice } = useCart();

  useEffect(() => {
    loadProductos();
  }, []);

  useEffect(() => {
    // Aplicar filtro de búsqueda
    if (searchTerm) {
      const filtered = productos.filter(producto =>
        producto.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
        producto.descripcion?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredProductos(filtered);
      setCurrentPage(1); // Resetear a página 1 al buscar
    } else {
      setFilteredProductos(productos);
    }
  }, [productos, searchTerm]);

  // Calcular productos paginados
  const totalPages = Math.ceil(filteredProductos.length / ITEMS_PER_PAGE);
  const paginatedProductos = filteredProductos.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const handlePageChange = (_event: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const loadProductos = async () => {
    try {
      setLoading(true);
      const availableProducts = await productoService.getProductos();
      // Filtrar solo productos activos con stock
      const filtered = availableProducts.filter(p => p.activo && p.stock_actual > 0);
      setProductos(filtered);
      console.log('✅ Productos cargados desde Django:', filtered.length);
    } catch (error) {
      console.error('❌ Error al cargar productos:', error);
      setProductos([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = (product: Producto) => {
    if (product.stock_actual > 0) {
      addToCart(product, 1);
      setSnackbar({
        open: true,
        message: `${product.nombre} agregado al carrito`,
        severity: 'success'
      });
    }
  };

  const handleWhatsApp = () => {
    const cartMessage = items.length > 0 
      ? `Hola! Quiero hacer un pedido:%0A${items.map(item => `• ${item.product.nombre} x${item.quantity} - ${formatPrice(item.product.precio_venta)}`).join('%0A')}%0A%0ATotal: ${formatPrice(getTotalPrice())}`
      : 'Hola! Quiero hacer un pedido';
    window.open(`https://wa.me/56949275045?text=${cartMessage}`, '_blank');
  };

  const getStockStatus = (producto: Producto) => {
    if (producto.stock_actual === 0) {
      return { label: 'Sin Stock', color: '#f44336', available: false };
    } else if (producto.stock_actual <= 10) {
      return { label: 'Pocas unidades', color: '#ff9800', available: true };
    } else {
      return { label: 'Disponible', color: '#4caf50', available: true };
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography variant="h6">Cargando productos...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Navegación con menú hamburguesa integrado */}
      <PublicNavigation />

      {/* Catálogo */}
      <CatalogContainer maxWidth="lg">
        {/* Header del catálogo */}
        <Box textAlign="center" mb={4}>
          <Typography 
            variant="h3" 
            component="h1" 
            sx={{ 
              fontFamily: "'Playfair Display', serif",
              color: '#2e5a33',
              fontWeight: 700,
              mb: 2
            }}
          >
            Nuestros Productos
          </Typography>
          <Typography variant="h6" color="textSecondary">
            Descubre nuestra selección de productos locales y artesanales
          </Typography>
        </Box>

        {/* Búsqueda */}
        <SearchContainer>
          <TextField
            fullWidth
            placeholder="Buscar productos por nombre, descripción o categoría..."
            variant="outlined"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'action.active' }} />,
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '10px',
              }
            }}
          />
          
          <Box mt={2} display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="body2" color="textSecondary">
              {filteredProductos.length} productos encontrados
            </Typography>
            {searchTerm && (
              <Button 
                variant="outlined" 
                size="small"
                onClick={() => setSearchTerm('')}
              >
                Limpiar búsqueda
              </Button>
            )}
          </Box>
        </SearchContainer>

        {/* Grid de productos */}
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
          gap: 3,
        }}>
          {paginatedProductos.map((producto) => {
            const stockStatus = getStockStatus(producto);
            return (
              <ProductCard key={producto.id}>
                <ProductImage 
                  src={producto.imagen || 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjgwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjgwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI2UwZTBlMCIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTYiIGZpbGw9IiM5OTk5OTkiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5TaW4gSW1hZ2VuPC90ZXh0Pjwvc3ZnPg=='} 
                  alt={producto.nombre}
                />
                
                <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                  <Typography 
                    variant="h6" 
                    component="h3" 
                    sx={{ 
                      fontWeight: 600, 
                      mb: 1,
                      fontSize: '1.1rem',
                      lineHeight: 1.3,
                      minHeight: '48px',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden'
                    }}
                  >
                    {producto.nombre}
                  </Typography>
                  
                  <Typography 
                    variant="body2" 
                    color="textSecondary" 
                    sx={{ 
                      mb: 2, 
                      flexGrow: 1,
                      minHeight: '40px',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden'
                    }}
                  >
                    {producto.descripcion}
                  </Typography>

                  <Box sx={{ mt: 'auto' }}>
                    {/* Chips de información */}
                    <Box sx={{ mb: 2 }}>
                      <StockChip 
                        available={stockStatus.available}
                        label={stockStatus.label}
                        size="small"
                      />
                      <Chip 
                        label={producto.categoria || 'General'}
                        size="small"
                        sx={{ ml: 1, backgroundColor: '#f5f5f5' }}
                      />
                    </Box>

                    {/* Precio */}
                    <PriceTag>
                      {formatPrice(producto.precio_venta)}
                    </PriceTag>

                    {/* Botón de agregar al carrito */}
                    <AddToCartButton
                      fullWidth
                      disabled={!stockStatus.available}
                      onClick={() => handleAddToCart(producto)}
                      startIcon={<ShoppingCartIcon />}
                    >
                      {stockStatus.available ? 'Agregar al carrito' : 'Sin stock'}
                    </AddToCartButton>
                  </Box>
                </CardContent>
              </ProductCard>
            );
          })}
        </Box>

        {/* Paginación */}
        {filteredProductos.length > ITEMS_PER_PAGE && (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            justifyContent: 'center', 
            alignItems: 'center', 
            py: 4, 
            gap: 2 
          }}>
            <Typography variant="body2" color="textSecondary">
              Mostrando {((currentPage - 1) * ITEMS_PER_PAGE) + 1} - {Math.min(currentPage * ITEMS_PER_PAGE, filteredProductos.length)} de {filteredProductos.length} productos
            </Typography>
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={handlePageChange}
              color="primary"
              size="large"
              showFirstButton
              showLastButton
              sx={{
                '& .MuiPaginationItem-root': {
                  fontWeight: 500,
                  fontSize: '1rem',
                  '&.Mui-selected': {
                    background: 'linear-gradient(135deg, #2e5a33 0%, #3d7342 100%)',
                    color: 'white',
                    boxShadow: '0 4px 12px rgba(46, 90, 51, 0.3)',
                  },
                  '&:hover': {
                    backgroundColor: 'rgba(46, 90, 51, 0.1)',
                  },
                },
              }}
            />
          </Box>
        )}

        {/* Mensaje cuando no hay productos */}
        {filteredProductos.length === 0 && (
          <Box textAlign="center" py={8}>
            <Typography variant="h5" color="textSecondary" mb={2}>
              No se encontraron productos
            </Typography>
            <Typography variant="body1" color="textSecondary" mb={3}>
              {searchTerm 
                ? `No hay productos que coincidan con "${searchTerm}"`
                : 'No hay productos disponibles en este momento'
              }
            </Typography>
            {searchTerm && (
              <Button 
                variant="contained" 
                onClick={() => setSearchTerm('')}
                sx={{
                  background: 'linear-gradient(135deg, #2e5a33 0%, #3d7342 100%)',
                }}
              >
                Ver todos los productos
              </Button>
            )}
          </Box>
        )}



        {/* Carrito Lateral */}
        <CartDrawer
          anchor="right"
          open={cartOpen}
          onClose={() => setCartOpen(false)}
        >
          <CartHeader>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              Mi Carrito ({getTotalItems()} {getTotalItems() === 1 ? 'artículo' : 'artículos'})
            </Typography>
            <IconButton 
              onClick={() => setCartOpen(false)}
              sx={{ color: 'white' }}
            >
              <CloseIcon />
            </IconButton>
          </CartHeader>

          <CartContent>
            {items.length === 0 ? (
              <EmptyCart>
                <ShoppingCartIcon sx={{ fontSize: 80, color: '#e0e0e0', mb: 2 }} />
                <Typography variant="h6" color="textSecondary" gutterBottom>
                  Tu carrito está vacío
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  ¡Agrega algunos productos para comenzar!
                </Typography>
              </EmptyCart>
            ) : (
              <Box>
                {items.map((item) => (
                  <CartItemCard key={item.product.id} elevation={0}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <CartItemImage
                        src={getProductImage(item.product)}
                        alt={item.product.nombre}
                      />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                          {item.product.nombre}
                        </Typography>
                        <Typography variant="body2" color="textSecondary" gutterBottom>
                          {formatPrice(item.product.precio_venta)} c/u
                        </Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                          <QuantityControls>
                            <QuantityButton
                              size="small"
                              onClick={() => item.product.id && updateQuantity(item.product.id, item.quantity - 1)}
                              disabled={item.quantity <= 1}
                            >
                              <Remove fontSize="small" />
                            </QuantityButton>
                            <Typography sx={{ px: 2, fontWeight: 'bold', minWidth: '40px', textAlign: 'center' }}>
                              {item.quantity}
                            </Typography>
                            <QuantityButton
                              size="small"
                              onClick={() => item.product.id && updateQuantity(item.product.id, item.quantity + 1)}
                              disabled={item.quantity >= item.product.stock_actual}
                            >
                              <Add fontSize="small" />
                            </QuantityButton>
                          </QuantityControls>
                          <Box sx={{ textAlign: 'right' }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#2e5a33' }}>
                              {formatPrice(item.product.precio_venta * item.quantity)}
                            </Typography>
                            <IconButton
                              size="small"
                              onClick={() => item.product.id && removeFromCart(item.product.id)}
                              sx={{ color: '#f44336', mt: 0.5 }}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  </CartItemCard>
                ))}
              </Box>
            )}
          </CartContent>

          {items.length > 0 && (
            <CartFooter>
              <TotalSection>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    Total:
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                    {formatPrice(getTotalPrice())}
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  {getTotalItems()} {getTotalItems() === 1 ? 'producto' : 'productos'}
                </Typography>
              </TotalSection>

              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={clearCart}
                  startIcon={<Delete />}
                  sx={{ 
                    borderColor: '#f44336', 
                    color: '#f44336',
                    '&:hover': {
                      borderColor: '#d32f2f',
                      backgroundColor: '#ffebee'
                    }
                  }}
                >
                  Vaciar
                </Button>
                <CheckoutButton
                  fullWidth
                  variant="contained"
                  onClick={() => {
                    navigate('/checkout');
                    setCartOpen(false);
                  }}
                  sx={{ mb: 1 }}
                >
                  Proceder al Checkout
                </CheckoutButton>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => {
                    handleWhatsApp();
                    setCartOpen(false);
                  }}
                  startIcon={<WhatsAppIcon />}
                  sx={{ 
                    borderColor: '#25D366',
                    color: '#25D366',
                    '&:hover': {
                      borderColor: '#128C7E',
                      backgroundColor: 'rgba(37, 211, 102, 0.04)'
                    }
                  }}
                >
                  Pedir por WhatsApp
                </Button>
              </Box>
            </CartFooter>
          )}
        </CartDrawer>

        {/* Snackbar para notificaciones */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert 
            onClose={() => setSnackbar({ ...snackbar, open: false })} 
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </CatalogContainer>
    </Box>
  );
};

export default ProductCatalog;